import { isPlainObject, omit } from 'lodash-es';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { JobPosition } from '@pflegia/types/api/job-position';
import { toStripSlugs } from '@pflegia/ui/shared/constants/navigation';

import {
  AnswerCallback,
  BaseFunnelAnswers,
  BaseFunnelJobTitle,
  BaseFunnelQuestion,
  FunnelJobTitleAnswer,
  FunnelNavItem,
  LocalFunnelStep,
  OnFunnelAnswer,
} from '../types';
import { getNextQuestionIndex } from '../utils/get-next-question-index';
import { questionsToFunnelNavItems } from '../utils/questions-to-funnel-nav-items';
import { useFunnelState } from './use-funnel-state';

/**
 * Main logic for local funnel.
 *
 * Requires `LocalFunnelStep[]` as input to determine order, conditions and job title updates.
 * Returns properties necessary for local funnel functionality.
 */
export const useFunnel = <
  J extends BaseFunnelJobTitle = BaseFunnelJobTitle,
  A extends BaseFunnelAnswers<J> = BaseFunnelAnswers<J>,
  Q extends string = string,
  P extends Record<string, unknown> | undefined = undefined,
>({
  jobTitles,
  onAnswer,
  questions,
  props,
  jobPosition,
  onFinish,
}: {
  jobTitles: J[];
  onAnswer?: AnswerCallback<Q>;
  questions: LocalFunnelStep<J, A, Q, P>[];
  props: P;
  // For MultiPosting Funnel
  jobPosition?: JobPosition;
  onFinish?: () => void;
}): {
  /**
   * Funnel navigation items and their completion states.
   */
  navigation: FunnelNavItem[];
  /**
   * Handles skipping the current question.
   */
  onSkip: () => void;
  /**
   * Submits an answer. If `answer` is not set will use the `value` set by `setValue`.
   */
  onAnswer: OnFunnelAnswer;
  /**
   * Current question.
   */
  question: Q | null;
  /**
   * Sets the `value` inner state.
   */
  setValue: Dispatch<SetStateAction<unknown | undefined>>;
  /**
   * Holds the current step's value set with `setValue`.
   */
  value: unknown;
  disableContinue: boolean;
  setDisableContinue: Dispatch<SetStateAction<boolean>>;
} => {
  const [value, setValue] = useState<unknown>();
  const [disableContinue, setDisableContinue] = useState(false);
  const answers = useStoreWithEqualityFn(useFunnelState, state => omit(state, ['clearAnswers', 'setAnswers']), shallow) as A;
  const setAnswers = useFunnelState(state => state.setAnswers);

  const router = useRouter();
  const questionId = router.query.id as string | undefined;

  // Calculate the first unanswered question index when no id is provided
  const initialQuestionIndex = useMemo(() => {
    if (questionId) {
      return questions.findIndex(question => question.key === questionId);
    }
    // If no id or invalid id, apply different logic based on skippable prop
    if (props?.skippable) {
      // For skippable funnels, find the first unanswered question
      if (jobTitles.length === 0) return 0; // Wait for job titles to load
      const nextIndex = getNextQuestionIndex(questions, answers, jobTitles, undefined, props);
      return nextIndex === -1 ? 0 : nextIndex;
    }
    // For non-skippable funnels, keep old behavior (always start at position 0)
    return 0;
  }, [questionId, questions, jobTitles, answers, props]);

  const currentQuestionKey = questions[initialQuestionIndex]?.key;
  const currentQuestionIndex = initialQuestionIndex;

  const navigation = useMemo<FunnelNavItem[]>(
    () => questionsToFunnelNavItems(answers, currentQuestionIndex, jobTitles, questions, props),
    [answers, currentQuestionIndex, jobTitles, props, questions],
  );

  useEffect(() => {
    // This is meant for MultiPosting Funnel, at the start of the funnel we want to append all of the current job positions job titles
    if (
      !jobPosition ||
      jobPosition?.jobTitles?.length === 0 ||
      jobPosition.jobTitles?.every(jobTitle =>
        answers[BaseFunnelQuestion.PositionDesired]?.some(answer => answer.jobTitle.id === jobTitle.id),
      )
    ) {
      return;
    }
    setAnswers({
      [BaseFunnelQuestion.PositionDesired]: jobPosition?.jobTitles?.map(jobTitle => ({
        jobTitle,
        question: BaseFunnelQuestion.PositionDesired,
      })),
    } as A);
  }, [answers, jobPosition, setAnswers]);

  // Helper function to navigate to a question using router.push with 'as' parameter
  const navigateToQuestionById = useCallback(
    (questionKey: string) => {
      const [pathname] = router.asPath.split('?');
      // Preserve existing query params and update id
      router.push(
        { pathname: router.pathname, query: { ...router.query, id: questionKey } },
        { pathname, query: { ...omit(router.query, toStripSlugs), id: questionKey } },
        { shallow: true },
      );
    },
    [router],
  );

  // Validate current question on route change, e.g. refreshed page or went back to previous one
  const hasValidatedInitialStep = useRef(false);
  useEffect(() => {
    // Wait for job titles query and router query params
    if (jobTitles.length === 0 || !router.isReady) return;

    const nextQuestionIndex = getNextQuestionIndex(
      questions,
      answers,
      jobTitles,
      props?.skippable ? currentQuestionIndex + 1 : undefined,
      props,
    );

    const isInitialLoad = !hasValidatedInitialStep.current;
    hasValidatedInitialStep.current = true;

    // No unfilled step left
    if (nextQuestionIndex === -1) return;

    // Initial load resumes at first unfilled step (either direction); later runs only pull back so back-nav works
    const stayOnCurrentStep =
      isInitialLoad && !props?.skippable ? currentQuestionIndex === nextQuestionIndex : currentQuestionIndex <= nextQuestionIndex;
    if (stayOnCurrentStep) return;

    navigateToQuestionById(questions[nextQuestionIndex].key);
  }, [answers, currentQuestionIndex, jobTitles, props, questions, navigateToQuestionById, router.isReady]);

  /**
   * Syncs the local `value` when the question changes:
   * - Restores the previously selected answer when navigating backward.
   * - Uses the question's default value (or undefined) when moving forward.
   * - Ensures forward navigation does not flash the Next button while keeping backward navigation UX intact.
   *
   * Separated the logic from the above useEffect to avoid props/jobTitles from causing renders
   * when not memorized since they runs from new object/array references.
   */
  const prevIndexRef = useRef<number | null>(null);
  useEffect(() => {
    const question = questions[currentQuestionIndex];
    const currentAnswer = answers[question.key];
    const goingBack = prevIndexRef.current !== null && currentQuestionIndex < prevIndexRef.current;

    // Set previous answer when going backwards in the funnel, otherwise fall back to default or undefined
    const nextValue = goingBack ? currentAnswer : (question.defaultValue ?? undefined);

    setValue(nextValue);
    prevIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex, questions, answers]);

  /**
   * Handles updating states when an answer is submitted.
   *
   * - Resets future answer values in case user went back in history.
   * - Updates job title if answer affected it.
   * - Tracks answers with analytics.
   * - Gets next question and navigates to it with router.
   */
  const handleAnswer = useCallback<OnFunnelAnswer>(
    answer => {
      // Reset future questions
      const futureQuestions = getFutureQuestionKeys(questions, currentQuestionIndex);

      const resetValues = props?.skippable ? {} : Object.fromEntries(futureQuestions.map(futureQuestion => [futureQuestion, null]));

      // Remove future answers from PositionDesired
      const currentJobTitleAnswers = answers[BaseFunnelQuestion.PositionDesired] ?? [];
      const resetJobTitleAnswers = currentJobTitleAnswers.filter(
        ({ question }) => question !== currentQuestionKey && !futureQuestions.includes(question as Q),
      );

      // Some questions would not call onAnswer upon answer update, hence we need to check value
      const newAnswer = (answer ?? value) as NonNullable<A[Q]>;

      const newAnswers = {
        ...resetValues,
        [BaseFunnelQuestion.PositionDesired]: resetJobTitleAnswers,
        [currentQuestionKey]: newAnswer,
      } as A;

      const { getNewJobTitle } = questions[currentQuestionIndex];
      if (getNewJobTitle) {
        const downgradedJobTitles = getNewJobTitle(newAnswer, answers, jobTitles, {
          jobPosition,
          ...(isPlainObject(props) ? props : { props }),
        });

        const newJobTitles = (Array.isArray(downgradedJobTitles) ? downgradedJobTitles : [downgradedJobTitles]).filter(Boolean);
        if (newJobTitles?.length) {
          newAnswers[BaseFunnelQuestion.PositionDesired] = resetJobTitleAnswers.concat(
            newJobTitles.map(jobTitle => ({ jobTitle, question: currentQuestionKey })) as FunnelJobTitleAnswer<J>[],
          );
        }
      }

      setAnswers(newAnswers);
      const latestAnswers = { ...answers, ...newAnswers };

      onAnswer?.(currentQuestionKey, questions[currentQuestionIndex].category, answer ?? value, latestAnswers);

      const nextQuestionIndex = getNextQuestionIndex(questions, latestAnswers, jobTitles, currentQuestionIndex + 1, props);

      if (nextQuestionIndex <= currentQuestionIndex) {
        if (onFinish) {
          onFinish();
        } else {
          router.push('/');
        }
      } else {
        navigateToQuestionById(questions[nextQuestionIndex].key);
      }
    },
    [
      answers,
      currentQuestionIndex,
      currentQuestionKey,
      jobPosition,
      jobTitles,
      navigateToQuestionById,
      onAnswer,
      onFinish,
      props,
      questions,
      router,
      setAnswers,
      value,
    ],
  );

  const handleSkip = useCallback(() => {
    const nextQuestionIndex = getNextQuestionIndex(questions, answers, jobTitles, currentQuestionIndex + 1, props);
    if (nextQuestionIndex > currentQuestionIndex) {
      navigateToQuestionById(questions[nextQuestionIndex].key);
    } else if (onFinish) {
      onFinish();
    } else {
      router.push('/');
    }
  }, [answers, currentQuestionIndex, jobTitles, navigateToQuestionById, onFinish, props, questions, router]);

  return {
    navigation,
    onAnswer: handleAnswer,
    onSkip: handleSkip,
    question: jobTitles.length ? currentQuestionKey : null,
    setValue,
    value,
    disableContinue,
    setDisableContinue,
  };
};

const getFutureQuestionKeys = <
  J extends BaseFunnelJobTitle = BaseFunnelJobTitle,
  A extends BaseFunnelAnswers<J> = BaseFunnelAnswers<J>,
  Q extends string = string,
  P extends Record<string, unknown> | undefined = undefined,
>(
  questions: LocalFunnelStep<J, A, Q, P>[],
  currentIndex: number,
) => questions.slice(currentIndex + 1).map(question => question.key);
